import classNames from 'classnames';
import { useEffect, useRef } from 'react';
import styled, { createGlobalStyle } from 'styled-components';

import { Link } from '@sorare/routing';

import { IconButton } from '#src/atoms/buttons/IconButton/index.tsx';
import { SorareLogo } from '#src/atoms/icons/SorareLogo/index.tsx';
import { iconSidebarLeft } from '#src/atoms/icons/system/iconSidebarLeft.ts';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '#src/atoms/layout/flex/index.tsx';
import {
  ANY_SPORT_SERIES_PLAY,
  LANDING,
} from '#src/constants/__generated__/routes.ts';
import { useSportContext } from '#src/contexts/sport/index.ts';
import { useIsLoggedIn } from '#src/hooks/auth/useIsLoggedIn.ts';
import { useEnableSetFeatures } from '#src/hooks/commonGame/useEnableSetFeatures.ts';
import { useIsInTraining } from '#src/hooks/commonGame/useIsInTraining.ts';
import { useSeriesNav } from '#src/hooks/commonGame/useSeriesNav.ts';
import { useIsLaptop } from '#src/hooks/device/useIsLaptop.ts';
import { useIsMobileApp } from '#src/hooks/useIsMobileApp.ts';
import { useLocalStorage } from '#src/hooks/useLocalStorage.ts';
import { generateSportPath } from '#src/lib/routing/generateSportPath.ts';
import { laptopAndAbove, laptopAndBelow } from '#src/style/mediaQuery.ts';

import {
  GlobalStylesWithNavBarWithSafeArea,
  GlobalStylesWithNavBarWithoutSafeArea,
  GlobalStylesWithoutNavBarWithSafeArea,
} from './GlobalStyles/index.tsx';
import { NavItems } from './NavItems/index.tsx';
import { ProSetToggle } from './ProSetToggle/index.tsx';
import { SearchBar } from './SearchBar/index.tsx';
import { SportSelector } from './SportSelector/index.tsx';
import { UserCurrencies } from './UserCurrencies/index.tsx';

const NAV_WIDTH = '240px';
const NAV_WIDTH_COLLAPSED = '68px';

const Root = styled(Vertical).attrs({ gap: 0 })`
  position: sticky;
  z-index: 2;
  bottom: 0;

  &.seriesNav {
    bottom: var(--double-unit);
    @media ${laptopAndAbove} {
      transition: unset;
    }
  }

  @media ${laptopAndAbove} {
    position: sticky;
    top: 0;
    height: 100vh;
    width: ${NAV_WIDTH};

    &.sidebarCollapsed {
      width: ${NAV_WIDTH_COLLAPSED};
    }
    transition: 300ms width ease-in-out;
  }
`;

// Lets fixed overlays offset past the sidebar to cover only the main content.
const NavigationWidthVar = createGlobalStyle<{ $width: string }>`
  :root {
    --app-navigation-width: ${({ $width }) => $width};
  }
`;
export const AbsoluteNav = createGlobalStyle`
  ${Root} & {
    @media ${laptopAndBelow} {
      position: fixed;
      width: 100%;
      left: 50%;
      transform: translateX(-50%);
    }
  }
`;
export const Nav = styled(Horizontal)`
  width: 100%;
  height: var(--bottom-bar-height-mobile);
  gap: 0;
  background-color: var(--c-surface-background);
  border-top: 1px solid var(--c-ui-secondary);

  .seriesNav & {
    background-color: transparent;
    border-top: none;
    width: unset;
    justify-content: center;
  }

  @media ${laptopAndAbove} {
    flex: 1;
    flex-direction: column;
    gap: var(--double-unit);
    border-top: none;
    border-right: 1px solid var(--c-ui-primary);
    padding: var(--intermediate-unit);
  }
`;

const FullWidthSBHorizontal = styled(SBHorizontal)`
  width: 100%;

  .sidebarCollapsed & {
    flex-direction: column;
  }
`;
const SportSelectorWrapper = styled(Horizontal).attrs({ gap: 0.5 })`
  .sidebarCollapsed & {
    flex-direction: column;
    gap: var(--double-unit);
  }
`;
const LogoWrapper = styled(Horizontal)`
  max-width: 100px;
  height: 40px;
`;
const PortalReceiver = styled(Vertical).attrs({ gap: 0 })`
  width: 100%;
  justify-content: flex-start;
  align-items: stretch;
`;
const CurrenciesWrapper = styled.div`
  width: 100%;
  margin-top: auto;
`;

export const MobileAppBottomPortalReceiver = styled(PortalReceiver)`
  padding-bottom: var(--double-unit);
  &.isAndroidApp {
    padding-bottom: 0;
  }
`;

const useHideAndroidNavigationBar = () => {
  const { postMessage, isFlutterApp } = useIsMobileApp();
  useEffect(() => {
    if (!isFlutterApp) {
      return () => {};
    }
    postMessage('toggleNavigationBar', {
      isVisible: false,
    });
    return () => {
      postMessage('toggleNavigationBar', {
        isVisible: true,
      });
    };
  }, [postMessage, isFlutterApp]);
};

const HideAndroidNavigationBar = () => {
  useHideAndroidNavigationBar();
  return null;
};

export const AppNavigation = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useLocalStorage(
    'navigationSidebarCollapsed',
    false
  );
  const { sport } = useSportContext();
  const navigationRef = useRef<HTMLElement>(null);
  const isLaptopAndAbove = useIsLaptop();
  const loggedIn = useIsLoggedIn();
  const { hideBottomNavBar, isAndroidApp } = useIsMobileApp();
  const seriesNav = useSeriesNav();
  useHideAndroidNavigationBar();
  const enableSetFeatures = useEnableSetFeatures();
  const isInTraining = useIsInTraining();
  const navigationWidth = isLaptopAndAbove
    ? sidebarCollapsed
      ? NAV_WIDTH_COLLAPSED
      : NAV_WIDTH
    : '0px';

  if (!loggedIn) {
    return null;
  }

  if (hideBottomNavBar) {
    return (
      <Root>
        <NavigationWidthVar $width="0px" />
        <GlobalStylesWithoutNavBarWithSafeArea />
        <PortalReceiver id="above-bottom-bar-portal" />
      </Root>
    );
  }

  return (
    <>
      <NavigationWidthVar $width={navigationWidth} />
      <HideAndroidNavigationBar />
      {isAndroidApp ? (
        <GlobalStylesWithNavBarWithoutSafeArea />
      ) : (
        <GlobalStylesWithNavBarWithSafeArea />
      )}
      <Root
        className={classNames({
          sidebarCollapsed: sidebarCollapsed && isLaptopAndAbove,
          seriesNav: seriesNav && !isLaptopAndAbove,
        })}
      >
        {!isLaptopAndAbove && <PortalReceiver id="above-bottom-bar-portal" />}
        <Nav as="nav" ref={navigationRef}>
          {!!isLaptopAndAbove && (
            <>
              <FullWidthSBHorizontal>
                {!sidebarCollapsed && (
                  <LogoWrapper
                    as={Link}
                    to={
                      seriesNav
                        ? generateSportPath(ANY_SPORT_SERIES_PLAY, { sport })
                        : generateSportPath(LANDING, { sport })
                    }
                  >
                    <SorareLogo size="small" />
                  </LogoWrapper>
                )}
                <SportSelectorWrapper>
                  <SportSelector withoutChevronIcon={sidebarCollapsed} />
                  {!!isLaptopAndAbove && (
                    <IconButton
                      size="medium"
                      color="transparent"
                      icon={iconSidebarLeft}
                      onClick={() => setSidebarCollapsed(prev => !prev)}
                    />
                  )}
                </SportSelectorWrapper>
              </FullWidthSBHorizontal>
              {!!enableSetFeatures && !isInTraining && (
                <ProSetToggle collapsed={sidebarCollapsed} />
              )}
              {!sidebarCollapsed && !isInTraining && <SearchBar />}
            </>
          )}
          <NavItems sport={sport} sidebarCollapsed={sidebarCollapsed} />
          {!!isLaptopAndAbove && !isInTraining && (
            <CurrenciesWrapper>
              <UserCurrencies sidebarCollapsed={sidebarCollapsed} />
            </CurrenciesWrapper>
          )}
        </Nav>
      </Root>
    </>
  );
};
